import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconoBorrar, IconoDescargar, IconoEtiqueta, IconoLeer, IconoPublicar } from '../components/Iconos';
import { AccionIcono } from '../components/AccionIcono';
import {
  MOTIVOS_DE_BAJA,
  api,
  type Category,
  type DocumentDetail,
  type DocumentVersion,
  type Group,
  type PendingReader,
  type VersionDeletionResult,
} from '../api/client';
import { relevoDe } from '../documentos/relevo';
import { useSession } from '../session/SessionContext';
import { formateadorDeDia } from '../formato';
import { AvisoError, AvisoExito } from '../components/Aviso';
import { Interruptor } from '../components/Interruptor';
import { GraficaTemporal, type PuntoTemporal } from '../components/GraficaTemporal';
import { BarrasComparativas } from '../components/BarrasComparativas';
import { CargaDeVersion } from '../components/CargaDeVersion';
import { SelectorDeGrupos } from '../components/SelectorDeGrupos';
import { Modal } from '../components/Modal';
import { LectoresDelDocumento } from '../components/LectoresDelDocumento';
import { claveVisible } from '../documentos/clave';

const TONO_ESTADO: Record<string, string> = {
  PUBLISHED: 'bueno',
  EXPIRED: 'aviso',
  DRAFT: 'neutro',
  UNPUBLISHED: 'critico',
  ARCHIVED: 'neutro',
};

/** Los campos de la ficha mientras se editan: todo texto, como en el formulario. */
interface Borrador {
  title: string;
  referenceCode: string;
  summary: string;
  categoryId: string;
  issuedOn: string;
  expiresOn: string;
  keywords: string;
}

function pesoLegible(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Ficha del documento en su propia ruta.
 *
 * Vivía apretada al lado del listado, en media pantalla. Un normativo tiene
 * metadatos, permisos, grupos e historial de versiones: eso pide la página
 * entera, y tener URL propia hace que se pueda enviar a alguien por correo.
 */
export function DocumentoDetalle({ grupos }: { grupos: Group[] }) {
  const { t, i18n } = useTranslation();
  const { puede, estado } = useSession();
  const puedeEliminar =
    estado.status === 'autenticado' && estado.session.canDeleteDocuments;
  const navegar = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();

  const [documento, setDocumento] = useState<DocumentDetail | null>(null);
  const [versiones, setVersiones] = useState<DocumentVersion[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [cargando, setCargando] = useState(true);
  /** La versión obligatoria por publicar, mientras se decide si avisar ya o a las 8:00. */
  const [publicando, setPublicando] = useState<{ id: string; etiqueta: string } | null>(null);
  const [serie, setSerie] = useState<PuntoTemporal[]>([]);
  const [alcance, setAlcance] = useState<{ leyeron: number; faltan: PendingReader[] } | null>(null);
  const [categorias, setCategorias] = useState<Category[]>([]);
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [audiencia, setAudiencia] = useState<string[] | null>(null);
  /** Qué ventana de edición está abierta. La ficha y la audiencia llevan su propio estado. */
  const [editando, setEditando] = useState<'permisos' | 'lectura' | null>(null);
  const [renombrando, setRenombrando] = useState<{ id: string; etiqueta: string } | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [eliminandoVersion, setEliminandoVersion] = useState<DocumentVersion | null>(null);

  const fecha = formateadorDeDia(i18n.language);

  const cargar = useCallback(async () => {
    if (!documentId) return;
    try {
      const [detalle, listaVersiones] = await Promise.all([
        api.document(documentId),
        api.documentVersions(documentId),
      ]);
      setDocumento(detalle);
      setVersiones(listaVersiones);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setCargando(false);
    }
  }, [documentId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // El comportamiento de lectura sólo lo ve quien puede consultar bitácoras: es
  // dato de personas, no del documento.
  useEffect(() => {
    if (!documentId || !puede('audit.read')) return;
    api
      .accessTimeline(`documentId=${documentId}`)
      .then((dias) =>
        setSerie(dias.map((dia) => ({ dia: dia.day, valores: [dia.sessions, dia.readers] }))),
      )
      .catch(setError);
    api
      .documentAccessReport(documentId)
      .then((informe) =>
        setAlcance({
          leyeron: informe.summary?.readers ?? 0,
          faltan: informe.neverOpened,
        }),
      )
      .catch(setError);
  }, [documentId, puede]);

  // Las categorías sólo se piden a quien puede editar: a los demás no les sirven
  // de nada y es una consulta por cada ficha que se abre.
  useEffect(() => {
    if (!puede('document.edit')) return;
    api.categories().then(setCategorias).catch(setError);
  }, [puede]);

  function abrirEdicion() {
    if (!documento) return;
    setMensaje(null);
    setBorrador({
      title: documento.title,
      referenceCode: documento.referenceCode ?? '',
      summary: documento.summary ?? '',
      categoryId: documento.categoryId,
      issuedOn: documento.issuedOn.slice(0, 10),
      expiresOn: documento.expiresOn?.slice(0, 10) ?? '',
      keywords: documento.keywords.join(', '),
    });
  }

  async function guardarFicha(evento: React.FormEvent) {
    evento.preventDefault();
    if (!documento || !borrador) return;
    setOcupado(true);
    setMensaje(null);
    try {
      setDocumento(
        await api.updateDocument(documento.id, {
          title: borrador.title.trim(),
          // Vacío lo borra: es un dato opcional y quitarlo tiene que poderse.
          referenceCode: borrador.referenceCode.trim() || null,
          summary: borrador.summary.trim(),
          categoryId: borrador.categoryId,
          issuedOn: borrador.issuedOn,
          // Un campo de fecha vacío es «sin vencimiento», no «no lo toques»:
          // enviarlo como cadena vacía sería un 400.
          ...(borrador.expiresOn ? { expiresOn: borrador.expiresOn } : {}),
          keywords: borrador.keywords
            .split(',')
            .map((palabra) => palabra.trim())
            .filter(Boolean),
        }),
      );
      setBorrador(null);
      setMensaje(t('documents.detailsSaved'));
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  async function guardarAudiencia() {
    if (!documento || !audiencia) return;
    setOcupado(true);
    setMensaje(null);
    try {
      setDocumento(await api.updateDocumentGroups(documento.id, audiencia));
      setAudiencia(null);
      setMensaje(t('documents.audienceSaved'));
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  const [descargando, setDescargando] = useState(false);

  /**
   * Pide la liga y deja que el navegador haga la descarga.
   *
   * No se abre en otra pestaña: una pestaña que se abre y se cierra sola parece
   * un fallo. Un ancla con `download` descarga sin moverse de la página.
   *
   * Son dos ligas distintas y el servidor dice cuál toca. Sin marca es una liga
   * firmada del almacén y el archivo sale de ahí. **Con marca no puede serlo**:
   * el almacén sólo tiene el original, y el original no lleva el nombre de
   * nadie — la copia marcada la arma la API, una por persona, y por eso esa se
   * baja contra la propia API y puede tardar lo que tarde en rasterizarse.
   */
  async function descargar() {
    if (!documento) return;
    setDescargando(true);
    setError(null);
    try {
      const liga = await api.downloadLink(documento.id);

      // Con marca se trae el archivo aquí en vez de soltar el ancla: así un 403
      // o un fallo a medio armar sale como aviso en la pantalla y no como una
      // descarga que el navegador cancela sin decir por qué.
      const href = liga.watermarked ? await api.descargarArchivo(liga.url) : liga.url;

      const ancla = document.createElement('a');
      ancla.href = href;
      ancla.download = liga.fileName;
      ancla.rel = 'noopener';
      document.body.appendChild(ancla);
      ancla.click();
      ancla.remove();
      if (liga.watermarked) URL.revokeObjectURL(href);
    } catch (e) {
      setError(e);
    } finally {
      setDescargando(false);
    }
  }

  async function cambiarPermiso(patch: {
    isPrintable?: boolean;
    isDownloadable?: boolean;
    printWatermark?: boolean;
    downloadWatermark?: boolean;
    mandatoryReading?: boolean;
    readingDeadlineDays?: number | undefined;
  }) {
    if (!documento) return;
    setOcupado(true);
    setMensaje(null);
    try {
      setDocumento(await api.updateDocument(documento.id, patch));
      setMensaje(t('documents.permissionSaved'));
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  async function subir(archivo: File, alAvanzar: (porcentaje: number) => void) {
    if (!documento) return;
    setOcupado(true);
    setError(null);
    setMensaje(null);
    try {
      const version = await api.uploadVersionConProgreso(documento.id, archivo, alAvanzar);
      setMensaje(t('documents.uploaded', { number: version.versionLabel }));
      await cargar();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Publicar. Si el documento es de lectura obligatoria, quien publica ya eligió
   * en la ventana si avisar ahora o dejarlo para la corrida de las 8:00, y el
   * mensaje dice qué pasó con el aviso.
   */
  async function publicar(versionId: string, avisarAhora = false) {
    if (!documento) return;
    setOcupado(true);
    setError(null);
    try {
      const publicado = await api.publishVersion(documento.id, versionId, avisarAhora);
      const aviso = publicado.notified;
      setMensaje(
        !documento.mandatoryReading
          ? t('documents.published')
          : !aviso
            ? t('documents.publishedDeferred')
            : aviso.error
              ? t('documents.publishedNotifyFailed')
              : aviso.failed > 0
                ? t('documents.publishedNotifiedPartial', { sent: aviso.sent, failed: aviso.failed })
                : aviso.sent === 0
                  ? t('documents.publishedNoAudience')
                  : t('documents.publishedNotified', { count: aviso.sent }),
      );
      setPublicando(null);
      await cargar();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  if (cargando) {
    return <p className="tenue">{t('app.loading')}</p>;
  }

  if (!documento) {
    return (
      <>
        <AvisoError error={error} />
        <div className="vacio">
          <span className="vacio__titulo">{t('documents.notFound')}</span>
          <Link className="boton boton--fantasma" to="/documentos">
            {t('documents.backToList')}
          </Link>
        </div>
      </>
    );
  }

  const nombresDeGrupo = documento.groupIds
    .map((id) => grupos.find((grupo) => grupo.id === id)?.name)
    .filter((nombre): nombre is string => Boolean(nombre));

  function abrir(cual: 'permisos' | 'lectura') {
    setMensaje(null);
    setError(null);
    setEditando(cual);
  }

  const sinMarca =
    (documento.isPrintable && !documento.printWatermark) ||
    (documento.isDownloadable && !documento.downloadWatermark);

  /**
   * Los permisos, igual en la tarjeta y en la ventana: en la tarjeta sólo se
   * leen, en la ventana se cambian. Un solo lugar que los dibuja evita que las
   * dos vistas digan cosas distintas.
   */
  const permisos = (editable: boolean) => (
    <div className="datos">
      <Interruptor
        etiqueta={t('documents.printable')}
        activo={documento.isPrintable}
        editable={editable}
        ocupado={ocupado}
        onCambio={(valor) => void cambiarPermiso({ isPrintable: valor })}
      />
      {/* El sub-interruptor sólo aparece cuando el de arriba está encendido:
          preguntar «¿con marca?» sobre algo que no se puede imprimir no tiene
          respuesta útil. */}
      {documento.isPrintable && (
        <Interruptor
          anidado
          etiqueta={t('documents.printWatermark')}
          activo={documento.printWatermark}
          editable={editable}
          ocupado={ocupado}
          textoActivo={t('documents.withWatermark')}
          textoInactivo={t('documents.withoutWatermark')}
          onCambio={(valor) => void cambiarPermiso({ printWatermark: valor })}
        />
      )}
      <Interruptor
        etiqueta={t('documents.downloadable')}
        activo={documento.isDownloadable}
        editable={editable}
        ocupado={ocupado}
        onCambio={(valor) => void cambiarPermiso({ isDownloadable: valor })}
      />
      {documento.isDownloadable && (
        <Interruptor
          anidado
          etiqueta={t('documents.downloadWatermark')}
          activo={documento.downloadWatermark}
          editable={editable}
          ocupado={ocupado}
          textoActivo={t('documents.withWatermark')}
          textoInactivo={t('documents.withoutWatermark')}
          onCambio={(valor) => void cambiarPermiso({ downloadWatermark: valor })}
        />
      )}
    </div>
  );

  const lectura = (editable: boolean) => (
    <div className="datos">
      <Interruptor
        etiqueta={t('documents.mandatoryReading')}
        activo={documento.mandatoryReading}
        editable={editable}
        ocupado={ocupado}
        textoActivo={t('documents.mandatoryOn')}
        textoInactivo={t('documents.mandatoryOff')}
        onCambio={(valor) =>
          void cambiarPermiso(
            valor
              ? // Al encender hace falta plazo: se propone uno y se puede cambiar
                // enseguida, en la misma ventana.
                { mandatoryReading: true, readingDeadlineDays: documento.readingDeadlineDays ?? 30 }
              : { mandatoryReading: false, readingDeadlineDays: undefined },
          )
        }
      />
      {documento.mandatoryReading &&
        (editable ? (
          <div className="dato dato--anidado">
            <label className="dato__etiqueta" htmlFor="plazoLectura">
              {t('documents.deadlineDays')}
            </label>
            <input
              id="plazoLectura"
              type="number"
              min={1}
              max={365}
              defaultValue={documento.readingDeadlineDays ?? 30}
              disabled={ocupado}
              // Al salir del campo, no en cada tecla: escribir «30» pasa por
              // «3», y guardar ese «3» pondría a media caja en mora.
              onBlur={(e) => {
                const dias = Number(e.target.value);
                if (dias >= 1 && dias <= 365 && dias !== documento.readingDeadlineDays) {
                  void cambiarPermiso({ readingDeadlineDays: dias });
                }
              }}
            />
            <span className="campo__ayuda">{t('documents.deadlineHelp')}</span>
          </div>
        ) : (
          <div className="dato dato--anidado">
            <span className="dato__etiqueta">{t('documents.deadlineDays')}</span>
            <span className="dato__valor">
              {t('documents.deadlineOf', { days: documento.readingDeadlineDays })}
            </span>
          </div>
        ))}
    </div>
  );

  return (
    <>
      <div className="pagina__encabezado pagina__encabezado--con-vuelta">
        <Link className="migaja" to="/documentos">
          ← {t('documents.backToList')}
        </Link>
        <h1 className="pagina__titulo">
          <span className="mono pagina__clave">{claveVisible(documento)}</span>
          {documento.title}
        </h1>
        <div className="pagina__acciones">
          <span className={`pildora pildora--${TONO_ESTADO[documento.status] ?? 'neutro'}`}>
            <span className="pildora__punto" aria-hidden="true" />
            {t(`document.status.${documento.status}`)}
          </span>
          {documento.currentVersionId && (
            <button
              className="boton"
              type="button"
              onClick={() => navegar(`/documentos/${documento.id}/leer`)}
            >
              {t('documents.readAction')}
            </button>
          )}
          {/* La descarga existía en el servidor desde el principio y nunca llegó
              a la pantalla: se podía habilitar en el documento y no había forma
              de descargarlo. Aparece sólo si el documento lo permite — quien no
              puede descargar no ve un botón que le va a rebotar. */}
          {/* Sólo la cuenta original del sistema: la API lo vuelve a comprobar,
              esto sólo evita ofrecer un botón que va a rebotar. */}
          {puedeEliminar && (
            <button
              className="boton boton--peligro"
              type="button"
              onClick={() => {
                setMensaje(null);
                setError(null);
                setEliminando(true);
              }}
            >
              {t('documents.deleteAction')}
            </button>
          )}
          {documento.currentVersionId && documento.isDownloadable && (
            <button
              className="boton boton--fantasma"
              type="button"
              disabled={descargando}
              onClick={() => void descargar()}
            >
              <IconoDescargar />
              {descargando ? t('documents.downloading') : t('documents.downloadAction')}
            </button>
          )}
        </div>
      </div>

      <AvisoError error={error} />
      {mensaje && <AvisoExito>{mensaje}</AvisoExito>}

      <div className="rejilla-detalle">
        {/* Las tarjetas son para leer; cada edición se abre en su ventana. Con
            la ficha, los permisos, la lectura obligatoria y la audiencia
            editándose en la misma página, no se distinguía qué se estaba
            cambiando de lo que sólo se estaba mostrando. */}
        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('documents.metadata')}</h2>
            {puede('document.edit') && (
              <button type="button" className="boton boton--discreto" onClick={abrirEdicion}>
                {t('common.edit')}
              </button>
            )}
          </div>
            <div className="datos">
              <div className="dato">
                <span className="dato__etiqueta">{t('documents.referenceCode')}</span>
                <span className="dato__valor dato__valor--mono">{documento.referenceCode ?? '—'}</span>
              </div>
              <div className="dato">
                <span className="dato__etiqueta">{t('documents.category')}</span>
                <span className="dato__valor">{documento.categoryName ?? '—'}</span>
              </div>
              <div className="dato">
                <span className="dato__etiqueta">{t('documents.issuedOn')}</span>
                <span className="dato__valor dato__valor--mono">
                  {fecha.format(new Date(documento.issuedOn))}
                </span>
              </div>
              <div className="dato">
                <span className="dato__etiqueta">{t('documents.validUntil')}</span>
                <span className="dato__valor dato__valor--mono">
                  {documento.expiresOn ? fecha.format(new Date(documento.expiresOn)) : '—'}
                </span>
              </div>
            </div>
        </section>

        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('documents.permissionsTitle')}</h2>
            {puede('document.edit') && (
              <button type="button" className="boton boton--discreto" onClick={() => abrir('permisos')}>
                {t('common.edit')}
              </button>
            )}
          </div>
          {sinMarca && <p className="aviso aviso--atencion">{t('documents.noWatermarkWarning')}</p>}
          {permisos(false)}
        </section>

        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('documents.mandatoryTitle')}</h2>
            {puede('document.edit') && (
              <button type="button" className="boton boton--discreto" onClick={() => abrir('lectura')}>
                {t('common.edit')}
              </button>
            )}
          </div>
          {lectura(false)}
        </section>

        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('documents.audience')}</h2>
            {puede('document.edit') && (
              <button type="button" className="boton boton--discreto" onClick={() => { setMensaje(null); setAudiencia(documento.groupIds); }}>
                {t('common.edit')}
              </button>
            )}
          </div>
          {nombresDeGrupo.length === 0 ? (
            <p className="vacio">{t('documents.noGroups')}</p>
          ) : (
            <ul className="lista-etiquetas">
              {nombresDeGrupo.map((nombre) => (
                <li key={nombre} className="etiqueta">
                  {nombre}
                </li>
              ))}
            </ul>
          )}
          <p className="tarjeta__pie">{t('documents.groupsHelp')}</p>
        </section>

        {/* Cada ventana muestra su propio error. El aviso de la página queda
            detrás de la ventana, y un error que no se ve es el que hace creer que
            se guardó algo que no se guardó: ya pasó con el alta de usuarios. */}
        {borrador && (
          <Modal titulo={t('documents.editMetadata')} onCerrar={() => setBorrador(null)}>
            <AvisoError error={error} />
                <form onSubmit={guardarFicha}>
                  <div className="campos">
                    <div className="campo">
                      {/* `documents.title` es el título de la pantalla —«Documentos
                          normativos»—, no el nombre de este campo. */}
                      <label htmlFor="titulo">{t('documents.titleField')}</label>
                      <input
                        id="titulo"
                        value={borrador.title}
                        required
                        minLength={3}
                        maxLength={250}
                        onChange={(e) => setBorrador({ ...borrador, title: e.target.value })}
                      />
                    </div>
                    <div className="campo">
                      <label htmlFor="codigoReferencia">{t('documents.referenceCode')}</label>
                      <input
                        id="codigoReferencia"
                        value={borrador.referenceCode}
                        maxLength={100}
                        autoComplete="off"
                        onChange={(e) => setBorrador({ ...borrador, referenceCode: e.target.value })}
                      />
                      <span className="campo__ayuda">{t('documents.referenceCodeHelp')}</span>
                    </div>
                    <div className="campo">
                      <label htmlFor="categoria">{t('documents.category')}</label>
                      <select
                        id="categoria"
                        value={borrador.categoryId}
                        onChange={(e) => setBorrador({ ...borrador, categoryId: e.target.value })}
                      >
                        {categorias.map((categoria) => (
                          <option key={categoria.id} value={categoria.id}>
                            {categoria.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="campo">
                      <label htmlFor="emitido">{t('documents.issuedOn')}</label>
                      <input
                        id="emitido"
                        type="date"
                        value={borrador.issuedOn}
                        required
                        onChange={(e) => setBorrador({ ...borrador, issuedOn: e.target.value })}
                      />
                    </div>
                    <div className="campo">
                      <label htmlFor="vence">{t('documents.validUntil')}</label>
                      <input
                        id="vence"
                        type="date"
                        value={borrador.expiresOn}
                        onChange={(e) => setBorrador({ ...borrador, expiresOn: e.target.value })}
                      />
                      <span className="campo__ayuda">{t('documents.expiryHelp')}</span>
                    </div>
                    <div className="campo">
                      <label htmlFor="claves">{t('documents.keywords')}</label>
                      <input
                        id="claves"
                        value={borrador.keywords}
                        onChange={(e) => setBorrador({ ...borrador, keywords: e.target.value })}
                      />
                      <span className="campo__ayuda">{t('documents.keywordsHelp')}</span>
                    </div>
                  </div>

                  <div className="campo" style={{ marginTop: '.8rem' }}>
                    <label htmlFor="resumen">{t('documents.summary')}</label>
                    <textarea
                      id="resumen"
                      rows={3}
                      maxLength={2000}
                      value={borrador.summary}
                      onChange={(e) => setBorrador({ ...borrador, summary: e.target.value })}
                    />
                  </div>

                  {/* La clave no se edita: es con la que un normativo se cita dentro
                      de otro y en actas. Cambiarla no corrige el documento, lo
                      sustituye por otro. */}
                  <p className="campo__ayuda" style={{ marginTop: '.7rem' }}>
                    {t('documents.codeFixed', { code: documento.code })}
                  </p>

                  <div className="tarjeta__acciones" style={{ marginTop: '.9rem' }}>
                    <button type="submit" className="boton" disabled={ocupado}>
                      {t('common.save')}
                    </button>
                    <button
                      type="button"
                      className="boton boton--fantasma"
                      disabled={ocupado}
                      onClick={() => setBorrador(null)}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
          </Modal>
        )}

        {editando === 'permisos' && (
          <Modal titulo={t('documents.permissionsTitle')} onCerrar={() => setEditando(null)}>
            <AvisoError error={error} />
            <p className="campo__ayuda" style={{ marginBottom: '.9rem' }}>
              {t('documents.permissionsHelp')}
            </p>
            {sinMarca && <p className="aviso aviso--atencion">{t('documents.noWatermarkWarning')}</p>}
            {permisos(true)}
            <Listo onListo={() => setEditando(null)} />
          </Modal>
        )}

        {editando === 'lectura' && (
          <Modal titulo={t('documents.mandatoryTitle')} onCerrar={() => setEditando(null)}>
            <AvisoError error={error} />
            <p className="campo__ayuda" style={{ marginBottom: '.9rem' }}>
              {t('documents.mandatoryHelp')}
            </p>
            {lectura(true)}
            <Listo onListo={() => setEditando(null)} />
          </Modal>
        )}

        {/* Lectura obligatoria: avisar ya, o en la corrida de las 8:00. */}
        {publicando && (
          <Modal
            titulo={t('documents.publishNotify.title', { label: publicando.etiqueta })}
            onCerrar={() => setPublicando(null)}
          >
            <AvisoError error={error} />
            <p>{t('documents.publishNotify.body', { title: documento.title })}</p>
            <p className="campo__ayuda">{t('documents.publishNotify.later')}</p>
            <div className="modal__acciones">
              <button className="boton boton--fantasma" type="button" onClick={() => setPublicando(null)}>
                {t('groups.cancel')}
              </button>
              <button
                className="boton boton--discreto"
                type="button"
                disabled={ocupado}
                onClick={() => void publicar(publicando.id, false)}
              >
                {t('documents.publishNotify.publishOnly')}
              </button>
              <button
                className="boton"
                type="button"
                disabled={ocupado}
                onClick={() => void publicar(publicando.id, true)}
              >
                {t('documents.publishNotify.publishAndNotify')}
              </button>
            </div>
          </Modal>
        )}

        {audiencia && (
          <Modal titulo={t('documents.audience')} onCerrar={() => setAudiencia(null)}>
            <AvisoError error={error} />
            <SelectorDeGrupos
                grupos={grupos}
                elegidos={audiencia}
                onCambio={setAudiencia}
                deshabilitado={ocupado}
                avisoVacio={{ texto: t('groups.noneChosenWarning'), grave: true }}
            incluyeRama
              />
              <div className="tarjeta__acciones" style={{ marginTop: '.9rem' }}>
                <button
                  type="button"
                  className="boton"
                  disabled={ocupado || audiencia.length === 0}
                  onClick={() => void guardarAudiencia()}
                >
                  {t('common.save')}
                </button>
                <button
                  type="button"
                  className="boton boton--fantasma"
                  disabled={ocupado}
                  onClick={() => setAudiencia(null)}
                >
                  {t('common.cancel')}
                </button>
              </div>
          </Modal>
        )}
      </div>

      {eliminando && (
        <EliminarDocumento
          documento={documento}
          onCerrar={() => setEliminando(false)}
          onEliminado={() =>
            navegar('/documentos', {
              state: { mensaje: t('documents.deleted', { title: documento.title }) },
            })
          }
        />
      )}

      {eliminandoVersion && (
        <EliminarVersion
          documento={documento}
          version={eliminandoVersion}
          versiones={versiones}
          onCerrar={() => setEliminandoVersion(null)}
          onEliminada={(resultado) => {
            setEliminandoVersion(null);
            setMensaje(
              resultado.restoredVersion
                ? t('documents.versionDeletedRestored', {
                    label: resultado.deletedVersion,
                    restored: resultado.restoredVersion,
                  })
                : t('documents.versionDeleted', { label: resultado.deletedVersion }),
            );
            void cargar();
          }}
        />
      )}

      {renombrando && (
        <Modal titulo={t('documents.relabelTitle')} onCerrar={() => setRenombrando(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const valor = String(new FormData(e.currentTarget).get('versionLabel') ?? '').trim();
              if (!valor) return;
              void (async () => {
                setOcupado(true);
                setError(null);
                try {
                  await api.renameVersion(documento.id, renombrando.id, valor);
                  setRenombrando(null);
                  setMensaje(t('documents.relabelDone', { label: valor }));
                  await cargar();
                } catch (e) {
                  setError(e);
                } finally {
                  setOcupado(false);
                }
              })();
            }}
          >
            <div className="campo">
              <label htmlFor="versionLabel">{t('documents.relabelField')}</label>
              <input
                id="versionLabel"
                name="versionLabel"
                defaultValue={renombrando.etiqueta}
                maxLength={24}
                required
                autoFocus
              />
              <span className="campo__ayuda">{t('documents.relabelHelp')}</span>
            </div>
            {/* Lo que hay que saber antes de pulsar: la etiqueta ya viajó
                impresa en las copias que la gente tiene. */}
            <p className="aviso aviso--atencion">{t('documents.relabelWarning')}</p>
            <div className="modal__acciones">
              <button
                className="boton boton--fantasma"
                type="button"
                onClick={() => setRenombrando(null)}
              >
                {t('common.cancel')}
              </button>
              <button className="boton" type="submit" disabled={ocupado}>
                {t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* La lista de lectores sólo existe si el documento es obligatorio: sin
          plazo no hay a quién le falte, y una tabla vacía en cada ficha enseña a
          no mirarla. */}
      {documento.mandatoryReading && puede('audit.read') && (
        <LectoresDelDocumento documentId={documento.id} />
      )}

      {puede('audit.read') && (
        <div className="rejilla">
          <section className="tarjeta">
            <div className="tarjeta__encabezado">
              <h2 className="tarjeta__titulo">{t('charts.readingOverTime')}</h2>
            </div>
            <GraficaTemporal
              puntos={serie}
              series={[
                { nombre: t('charts.readings'), color: 'var(--serie-1)' },
                { nombre: t('charts.readers'), color: 'var(--serie-2)' },
              ]}
            />
            <p className="tarjeta__pie">{t('charts.readingOverTimeNote')}</p>
          </section>

          <section className="tarjeta">
            <div className="tarjeta__encabezado">
              <h2 className="tarjeta__titulo">{t('charts.coverage')}</h2>
              {alcance && (
                <span className="tarjeta__nota">
                  {alcance.leyeron + alcance.faltan.length > 0
                    ? `${Math.round(
                        (alcance.leyeron / (alcance.leyeron + alcance.faltan.length)) * 100,
                      )}%`
                    : '—'}
                </span>
              )}
            </div>
            {alcance && (
              <BarrasComparativas
                unidad={t('dashboard.people')}
                datos={[
                  { clave: 'leyeron', etiqueta: t('charts.read'), valor: alcance.leyeron },
                  { clave: 'faltan', etiqueta: t('charts.pending'), valor: alcance.faltan.length },
                ]}
              />
            )}
            <p className="tarjeta__pie">{t('charts.coverageNote')}</p>
          </section>
        </div>
      )}

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('documents.versions')}</h2>
          <span className="tarjeta__nota">{versiones.length}</span>
        </div>

        {puede('document.upload') && <CargaDeVersion ocupado={ocupado} onSubir={subir} />}

        {versiones.length === 0 ? (
          <p className="vacio">{t('documents.noVersions')}</p>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('documents.version')}</th>
                  <th>{t('documents.size')}</th>
                  <th>{t('documents.pages')}</th>
                  <th>{t('documents.antivirus')}</th>
                  <th>{t('users.status')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {versiones.map((version) => {
                  // La vigente es la que rige. Que se vea de un golpe evita el
                  // error caro: leer o publicar sobre la versión equivocada.
                  const esVigente = version.id === documento.currentVersionId;
                  // Una versión que no pasó el antivirus o cuya huella no cuadra
                  // no se abre: el visor no es el lugar para descubrirlo.
                  const seAbre =
                    version.avStatus === 'CLEAN' && version.status !== 'INTEGRITY_FAILED';
                  return (
                    <tr key={version.id} className={esVigente ? 'fila--vigente' : undefined}>
                      <td className="mono">
                        {esVigente && <span className="marca-vigente" aria-hidden="true" />}
                        {t('documents.versionShort', { number: version.versionLabel })}
                      </td>
                      <td className="mono">{pesoLegible(version.sizeBytes)}</td>
                      <td className="mono">{version.pageCount ?? '—'}</td>
                      <td>
                        <span
                          className={`pildora pildora--${version.avStatus === 'CLEAN' ? 'bueno' : 'aviso'}`}
                        >
                          <span className="pildora__punto" aria-hidden="true" />
                          {t(`document.av.${version.avStatus}`)}
                        </span>
                      </td>
                      <td>
                        <span className={`pildora pildora--${esVigente ? 'bueno' : 'neutro'}`}>
                          <span className="pildora__punto" aria-hidden="true" />
                          {esVigente
                            ? t('documents.currentVersion')
                            : t(`document.versionStatus.${version.status}`)}
                        </span>
                      </td>
                      <td>
                        <div className="acciones-fila">
                          {seAbre && (
                            <AccionIcono
                              etiqueta={esVigente ? t('documents.readAction') : t('documents.readOldAction')}
                              icono={<IconoLeer />}
                              to={`/documentos/${documento.id}/leer/${version.id}`}
                            />
                          )}
                          {/* Corregir cómo se llama la versión. Hace falta para
                              alinear la carga inicial con la numeración que la
                              caja ya traía en sus carpetas. */}
                          {puede('document.edit') && (
                            <AccionIcono
                              etiqueta={t('documents.relabelAction')}
                              icono={<IconoEtiqueta />}
                              disabled={ocupado}
                              onClick={() => {
                                setMensaje(null);
                                setRenombrando({ id: version.id, etiqueta: version.versionLabel });
                              }}
                            />
                          )}
                          {puede('document.publish') && version.status === 'UPLOADED' && (
                            <AccionIcono
                              etiqueta={t('documents.publishAction')}
                              icono={<IconoPublicar />}
                              tono="principal"
                              disabled={ocupado}
                              onClick={() =>
                                documento.mandatoryReading
                                  ? setPublicando({ id: version.id, etiqueta: version.versionLabel })
                                  : void publicar(version.id)
                              }
                            />
                          )}
                          {/* La baja de una versión: el archivo equivocado, sobre
                              todo. Sólo la cuenta original, como la del documento. */}
                          {puedeEliminar && (
                            <AccionIcono
                              etiqueta={t('documents.deleteVersionAction')}
                              icono={<IconoBorrar />}
                              tono="peligro"
                              disabled={ocupado}
                              onClick={() => {
                                setMensaje(null);
                                setError(null);
                                setEliminandoVersion(version);
                              }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="tarjeta__pie">{t('documents.publishNote')}</p>
      </section>
    </>
  );
}

/**
 * Eliminar un documento, con motivo.
 *
 * El motivo es de un catálogo con los más comunes y «otro», que obliga a
 * escribirlo. Se dice antes de pulsar qué pasa: el documento sale del portal
 * para todos, pero su historia —quién lo leyó, quién acusó— se queda, y la baja
 * queda en la bitácora con quién la hizo y por qué.
 */
function EliminarDocumento({
  documento,
  onCerrar,
  onEliminado,
}: {
  documento: DocumentDetail;
  onCerrar: () => void;
  onEliminado: () => void;
}) {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState<string>('');
  const [detalle, setDetalle] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  const listo = motivoCompleto(motivo, detalle);

  async function eliminar() {
    setOcupado(true);
    setError(null);
    try {
      await api.deleteDocument(documento.id, motivo, detalle.trim() || undefined);
      onEliminado();
    } catch (e) {
      setError(e);
      setOcupado(false);
    }
  }

  return (
    <Modal titulo={t('documents.deleteTitle', { title: documento.title })} onCerrar={onCerrar}>
      <AvisoError error={error} />
      <p className="aviso aviso--atencion">{t('documents.deleteWarning')}</p>

      <MotivoDeBaja
        motivo={motivo}
        detalle={detalle}
        onMotivo={setMotivo}
        onDetalle={setDetalle}
        nombres="documents.deletionReasons"
      />

      <div className="modal__acciones">
        <button className="boton boton--fantasma" type="button" onClick={onCerrar}>
          {t('common.cancel')}
        </button>
        <button
          className="boton boton--peligro"
          type="button"
          disabled={!listo || ocupado}
          onClick={() => void eliminar()}
        >
          {ocupado ? t('documents.deleting') : t('documents.deleteConfirm')}
        </button>
      </div>
    </Modal>
  );
}

/** Un motivo del catálogo y, si es «otro», explicado con al menos 10 caracteres. */
function motivoCompleto(motivo: string, detalle: string): boolean {
  return Boolean(motivo) && (motivo !== 'OTHER' || detalle.trim().length >= 10);
}

/**
 * El motivo de una baja: uno del catálogo y, con «otro», la explicación
 * obligatoria. Lo comparten la baja del documento y la de una versión; cambian
 * sólo los nombres de los motivos, que concuerdan con lo que se elimina.
 */
function MotivoDeBaja({
  motivo,
  detalle,
  onMotivo,
  onDetalle,
  nombres,
}: {
  motivo: string;
  detalle: string;
  onMotivo: (codigo: string) => void;
  onDetalle: (texto: string) => void;
  nombres: 'documents.deletionReasons' | 'documents.versionDeletionReasons';
}) {
  const { t } = useTranslation();
  const otro = motivo === 'OTHER';
  return (
    <>
      <fieldset className="campo">
        <legend>{t('documents.deleteReason')}</legend>
        <div className="motivos" role="radiogroup">
          {MOTIVOS_DE_BAJA.map((codigo) => (
            <label
              key={codigo}
              className={`casilla-ficha casilla-ficha--opcion${motivo === codigo ? ' casilla-ficha--marcada' : ''}`}
            >
              <input
                type="radio"
                name="motivo"
                value={codigo}
                checked={motivo === codigo}
                onChange={() => onMotivo(codigo)}
              />
              <span className="casilla-ficha__marca casilla-ficha__marca--redonda" aria-hidden="true" />
              <span className="casilla-ficha__nombre">{t(`${nombres}.${codigo}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="campo campo--anidado">
        <label htmlFor="detalleBaja">
          {otro ? t('documents.deleteDetailRequired') : t('documents.deleteDetailOptional')}
        </label>
        <textarea
          id="detalleBaja"
          rows={3}
          maxLength={500}
          value={detalle}
          onChange={(e) => onDetalle(e.target.value)}
        />
        {otro && <span className="campo__ayuda">{t('documents.deleteDetailHelp')}</span>}
      </div>
    </>
  );
}

/**
 * Eliminar una versión, con motivo.
 *
 * Para lo que más se usa —deshacer la publicación del archivo equivocado— la
 * ventana dice **antes** de pulsar qué pasa con la vigente: cuál vuelve a
 * regir, o por qué no se puede si no hay ninguna que la releve. Lo que de verdad
 * pasó lo confirma después la respuesta del servidor.
 */
function EliminarVersion({
  documento,
  version,
  versiones,
  onCerrar,
  onEliminada,
}: {
  documento: DocumentDetail;
  version: DocumentVersion;
  versiones: DocumentVersion[];
  onCerrar: () => void;
  onEliminada: (resultado: VersionDeletionResult) => void;
}) {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState<string>('');
  const [detalle, setDetalle] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  const relevo = relevoDe(versiones, version.id, documento.currentVersionId);
  const titulo = t('documents.deleteVersionTitle', { label: version.versionLabel });

  async function eliminar() {
    setOcupado(true);
    setError(null);
    try {
      onEliminada(
        await api.deleteVersion(documento.id, version.id, motivo, detalle.trim() || undefined),
      );
    } catch (e) {
      setError(e);
      setOcupado(false);
    }
  }

  if (relevo === 'ninguna') {
    return (
      <Modal titulo={titulo} onCerrar={onCerrar}>
        <p className="aviso aviso--atencion">{t('documents.deleteVersionBlocked')}</p>
        <div className="modal__acciones">
          <button className="boton" type="button" onClick={onCerrar}>
            {t('common.understood')}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal titulo={titulo} onCerrar={onCerrar}>
      <AvisoError error={error} />
      {relevo && (
        <p className="aviso aviso--atencion">
          {t('documents.deleteVersionCurrentWarning', { restored: relevo.versionLabel })}
        </p>
      )}
      {relevo && documento.mandatoryReading && (
        <p className="aviso aviso--info">
          {t('documents.deleteVersionMandatoryNote', { restored: relevo.versionLabel })}
        </p>
      )}
      <p className="aviso">{t('documents.deleteVersionWarning', { label: version.versionLabel })}</p>

      <MotivoDeBaja
        motivo={motivo}
        detalle={detalle}
        onMotivo={setMotivo}
        onDetalle={setDetalle}
        nombres="documents.versionDeletionReasons"
      />

      <div className="modal__acciones">
        <button className="boton boton--fantasma" type="button" onClick={onCerrar}>
          {t('common.cancel')}
        </button>
        <button
          className="boton boton--peligro"
          type="button"
          disabled={!motivoCompleto(motivo, detalle) || ocupado}
          onClick={() => void eliminar()}
        >
          {ocupado ? t('documents.deleting') : t('documents.deleteVersionConfirm')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Cierra una ventana cuyos cambios ya se guardaron. Los interruptores guardan al
 * tocarse, así que no hay nada que «guardar»: el botón dice lo que pasa.
 */
function Listo({ onListo }: { onListo: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="tarjeta__acciones" style={{ marginTop: '.9rem' }}>
      <button type="button" className="boton" onClick={onListo}>
        {t('common.done')}
      </button>
    </div>
  );
}
